// Detection API client — Tier 0 surface (see docs/SCAN-ROADMAP.md).
// Routes mirror /api/v1/detection/* on the backend.
//
// Tier 0 returns mostly empty data: no scanners are enabled in the
// in-process registry yet, so scan_jobs rows stay 'skipped' and the
// findings list is empty. The client surface lives here in full so
// Tier 1 can flip the scanner enabled flag and the UI populates
// without further client-layer changes.

import useSWR from 'swr';

export type FindingSeverity = 'info' | 'low' | 'medium' | 'high' | 'critical';
export type FindingStatus = 'open' | 'acknowledged' | 'suppressed' | 'fixed';

export interface Finding {
  id: string;
  sha256: string;
  artifact_id: string;
  artifact_path: string;
  repo_slug: string;
  project_slug: string;
  project_id: string;
  format: string;
  scanner_id: string;
  rule_id: string;
  severity: FindingSeverity;
  status: FindingStatus;
  title: string;
  detail: string;
  location?: string;
  fix_hint?: string;
  evidence?: Record<string, unknown>;
  first_seen_at: string;
  last_seen_at: string;
  suppressed_at?: string | null;
  suppressed_by?: string;
  suppress_reason?: string;
  // v181 Tier 5 SLA tracking. age_days is computed server-side
  // (PG `EXTRACT(EPOCH FROM now() - first_seen_at) / 86400`).
  age_days?: number;
  sla_breached_at?: string | null;
}

export interface ScanRules {
  project_id: string;
  enabled: boolean;
  fail_on: FindingSeverity;
  scanner_set: string[];
  path_excludes: string[];
  suppressions: Suppression[];
  // v169 Tier 2: per-scanner JSON config keyed by scanner_id.
  // Documented shapes:
  //   orbital.license.v1:    { allow: string[] }    // SPDX IDs
  //   orbital.banned-api.v1: { patterns: BannedApiPattern[] }
  //   orbital.secret.v1:     { extra_patterns: SecretPattern[] }
  scanner_config?: Record<string, Record<string, unknown>>;
  // v181 Tier 5: per-severity time-to-fix in days. Missing keys mean
  // "no SLA, never breach" — useful for muting `info` entirely.
  sla_targets?: Partial<Record<FindingSeverity, number>>;
}

export interface BannedApiPattern {
  rule_id: string;
  title?: string;
  regex: string;
  severity: FindingSeverity;
  message?: string;
  extensions?: string[];
}

export interface SecretExtraPattern {
  rule_id: string;
  title: string;
  regex: string;
  severity: FindingSeverity;
}

export interface Suppression {
  rule_id: string;
  reason: string;
  until?: string;
}

export interface Scanner {
  id: string;
  kind: string;
  version: string;
  rules_version: string;
  enabled: boolean;
  description: string;
  cost_class: 'cheap' | 'medium' | 'heavy';
}

export interface QueueStats {
  pending: number;
  claimed: number;
  running: number;
  done: number;
  failed: number;
  skipped: number;
  oldest_pending_sec: number;
}

const json = (url: string) => fetch(url, { credentials: 'include' }).then((r) => {
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json();
});

export interface FindingFilters {
  status?: FindingStatus;
  severity?: FindingSeverity;
  project?: string;
  scanner?: string;
  limit?: number;
}

function buildFindingsURL(f?: FindingFilters): string {
  const u = new URL('/api/v1/detection/findings', globalThis.location.origin);
  if (f?.status) u.searchParams.set('status', f.status);
  if (f?.severity) u.searchParams.set('severity', f.severity);
  if (f?.project) u.searchParams.set('project', f.project);
  if (f?.scanner) u.searchParams.set('scanner', f.scanner);
  if (f?.limit) u.searchParams.set('limit', String(f.limit));
  return u.pathname + u.search;
}

export function useFindings(filters?: FindingFilters) {
  return useSWR<Finding[]>(buildFindingsURL(filters), json, {
    refreshInterval: 30_000,
  });
}

export function useFinding(id: string | undefined) {
  return useSWR<Finding>(
    id ? `/api/v1/detection/findings/${id}` : null,
    json,
  );
}

export async function patchFindingStatus(
  id: string,
  status: FindingStatus,
  suppressReason?: string,
): Promise<void> {
  const res = await fetch(`/api/v1/detection/findings/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, suppress_reason: suppressReason ?? '' }),
  });
  if (!res.ok) throw new Error(`patch finding: ${res.status} ${await res.text()}`);
}

export function useScanRules(projectID: string | undefined) {
  return useSWR<ScanRules>(
    projectID ? `/api/v1/detection/rules?project=${encodeURIComponent(projectID)}` : null,
    json,
  );
}

export async function saveScanRules(projectID: string, rules: Omit<ScanRules, 'project_id'>): Promise<void> {
  const res = await fetch(
    `/api/v1/detection/rules?project=${encodeURIComponent(projectID)}`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rules),
    },
  );
  if (!res.ok) throw new Error(`save rules: ${res.status} ${await res.text()}`);
}

export function useScanners() {
  return useSWR<Scanner[]>('/api/v1/detection/scanners', json);
}

export function useQueueStats() {
  return useSWR<QueueStats>('/api/v1/detection/queue', json, {
    refreshInterval: 10_000,
  });
}

export async function enqueueRescan(input: {
  artifact_id: string;
  scanners?: string[];
}): Promise<{ enqueued: number }> {
  const res = await fetch('/api/v1/detection/jobs', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`enqueue: ${res.status} ${await res.text()}`);
  return res.json();
}

// runBackfill enqueues every (artifact, enabled-scanner) pair that
// doesn't already have a queue row, at backlog priority. Admin-only;
// returns the count enqueued. Optionally project-scoped.
export async function runBackfill(projectID?: string): Promise<{ enqueued: number; project: string; priority: number }> {
  const url = projectID
    ? `/api/v1/detection/backfill?project=${encodeURIComponent(projectID)}`
    : '/api/v1/detection/backfill';
  const res = await fetch(url, { method: 'POST', credentials: 'include' });
  if (!res.ok) throw new Error(`backfill: ${res.status} ${await res.text()}`);
  return res.json();
}

// --- v177 Tier 4 ---

export interface StatsRow {
  key: string;
  label?: string;
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

export function useStatsByCVE() {
  return useSWR<StatsRow[]>('/api/v1/detection/stats/by-cve', json, { refreshInterval: 60_000 });
}

export function useStatsByProject() {
  return useSWR<StatsRow[]>('/api/v1/detection/stats/by-project', json, { refreshInterval: 60_000 });
}

export function useStatsByPackage() {
  return useSWR<StatsRow[]>('/api/v1/detection/stats/by-package', json, { refreshInterval: 60_000 });
}

export interface FindingEvent {
  id: number;
  kind: 'opened' | 'acknowledged' | 'suppressed' | 'fixed' | 'reopened' | 'auto-reopened';
  severity: FindingSeverity;
  actor: string;
  reason: string;
  until?: string;
  occurred_at: string;
}

export function useFindingEvents(id: string | undefined) {
  return useSWR<FindingEvent[]>(id ? `/api/v1/detection/findings/${id}/events` : null, json);
}

// bulkPatchFindings patches many findings in a single call.
export async function bulkPatchFindings(input: {
  ids: string[];
  status: FindingStatus;
  suppress_reason?: string;
  suppressed_until?: string; // RFC3339 or undefined = indefinite
}): Promise<{ requested: number; matched: number; updated: number }> {
  const res = await fetch('/api/v1/detection/findings', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`bulk patch: ${res.status} ${await res.text()}`);
  return res.json();
}

// patchFinding extends the v161 single-PATCH path with the v177
// suppressed_until + reason fields. Status-only callers can omit
// the optional fields.
export async function patchFinding(id: string, input: {
  status: FindingStatus;
  suppress_reason?: string;
  suppressed_until?: string;
}): Promise<void> {
  const res = await fetch(`/api/v1/detection/findings/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`patch finding: ${res.status} ${await res.text()}`);
}

export function vexExportURL(projectIDOrSlug: string): string {
  return `/api/v1/detection/vex?project=${encodeURIComponent(projectIDOrSlug)}`;
}

// v181 Tier 5 finding diff vs the previous version of the same
// (repo, path) artifact line.
export interface FindingDiffEntry {
  scanner_id: string;
  rule_id: string;
  severity: FindingSeverity;
  title: string;
  location?: string;
}

export interface FindingDiff {
  current_version?: string;
  previous_version?: string;
  added: FindingDiffEntry[];
  persisted: FindingDiffEntry[];
  resolved: FindingDiffEntry[];
}

export function useFindingDiff(id: string | undefined) {
  return useSWR<FindingDiff>(
    id ? `/api/v1/detection/findings/${id}/diff` : null,
    json,
  );
}
